import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import toast from "react-hot-toast";
import { suiteService } from "../services/suiteService";
import { getStoredClientId } from "../utils/tokenUtils";

const SuiteFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  projectId,
  clientId,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          name: initialData.name || "",
          description: initialData.description || "",
        });
      } else {
        setFormData({
          name: "",
          description: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate
      const newErrors = {};
      if (!formData.name.trim()) {
        newErrors.name = "Suite name is required";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }

      const effectiveClientId = clientId || getStoredClientId();

      if (initialData) {
        // Update existing suite
        await suiteService.updateSuite(
          initialData.suite_id,
          projectId,
          effectiveClientId,
          {
            name: formData.name.trim(),
            description: formData.description.trim() || null,
          },
        );
        toast.success("Suite updated successfully!");
      } else {
        // Create new suite
        await suiteService.createSuite({
          project_id: projectId,
          client_id: effectiveClientId,
          name: formData.name.trim(),
          description: formData.description.trim() || null,
        });
        toast.success("Suite created successfully!");
      }

      // Refresh list first, then close modal
      if (onSubmit) {
        await onSubmit();
      }
      onClose();
    } catch (error) {
      console.error("Error saving suite:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save suite";
      toast.error(errorMessage);
      if (error.response?.data?.code === "DUPLICATE_SUITE_NAME") {
        setErrors({ name: "A suite with this name already exists" });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {initialData ? "Edit Suite" : "Create New Suite"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label htmlFor="name" className="text-gray-700">
              Suite Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              className={`mt-1 ${errors.name ? "border-red-300" : ""}`}
              placeholder="Enter suite name"
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-700">
              Description
            </Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={loading}
              rows={3}
              className="mt-1"
              placeholder="Enter suite description (optional)"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              onClick={onClose}
              disabled={loading}
              variant="outline"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? "Saving..."
                : initialData
                  ? "Update Suite"
                  : "Create Suite"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuiteFormModal;
